import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type {
  ModelWithFilesAndLinks,
  ModelFile,
  ModelLink,
  ModelQuery,
  CreateModelInput,
  UpdateModelInput,
  AddModelFileInput,
  AddModelLinkInput,
  ExternalModelMetadata,
} from '@maker-toolchest/shared';
import { modelsApi, type PaginatedResult } from './api';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const modelKeys = {
  all: ['models'] as const,
  lists: () => [...modelKeys.all, 'list'] as const,
  list: (query?: ModelQuery) => [...modelKeys.lists(), query] as const,
  details: () => [...modelKeys.all, 'detail'] as const,
  detail: (id: string) => [...modelKeys.details(), id] as const,
  linkDomains: () => [...modelKeys.all, 'link-domains'] as const,
};

// ============================================================================
// MODEL HOOKS
// ============================================================================

export function useModels(
  query?: ModelQuery,
  options?: Omit<UseQueryOptions<PaginatedResult<ModelWithFilesAndLinks>>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: modelKeys.list(query),
    queryFn: () => modelsApi.list(query),
    placeholderData: (previousData) => previousData,
    ...options,
  });
}

export function useModel(
  id: string,
  options?: Omit<UseQueryOptions<ModelWithFilesAndLinks>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: modelKeys.detail(id),
    queryFn: () => modelsApi.getById(id),
    enabled: !!id,
    ...options,
  });
}

export function useCreateModel(
  options?: UseMutationOptions<ModelWithFilesAndLinks, Error, CreateModelInput>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: modelsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
    },
    ...options,
  });
}

export function useUpdateModel(
  options?: UseMutationOptions<
    ModelWithFilesAndLinks,
    Error,
    { id: string; input: UpdateModelInput }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }) => modelsApi.update(id, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: modelKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
    },
    ...options,
  });
}

export function useDeleteModel(options?: UseMutationOptions<void, Error, string>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: modelsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
      // Invalidate storage stats since model (and its files) were deleted
      queryClient.invalidateQueries({ queryKey: ['stats', 'storage'] });
    },
    ...options,
  });
}

// ============================================================================
// FILE HOOKS
// ============================================================================

export function useAddFile(
  options?: UseMutationOptions<
    ModelFile,
    Error,
    { modelId: string; file: File; input: AddModelFileInput }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ modelId, file, input }) => modelsApi.addFile(modelId, file, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: modelKeys.detail(variables.modelId) });
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
      // Invalidate storage stats since file was uploaded
      queryClient.invalidateQueries({ queryKey: ['stats', 'storage'] });
    },
    ...options,
  });
}

export function useDeleteFile(
  options?: UseMutationOptions<void, Error, { modelId: string; fileId: string }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ modelId, fileId }) => modelsApi.deleteFile(modelId, fileId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: modelKeys.detail(variables.modelId) });
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
      // Invalidate storage stats since file was deleted
      queryClient.invalidateQueries({ queryKey: ['stats', 'storage'] });
    },
    ...options,
  });
}

// ============================================================================
// LINK HOOKS
// ============================================================================

export function useAddLink(
  options?: UseMutationOptions<
    ModelLink,
    Error,
    { modelId: string; input: AddModelLinkInput }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ modelId, input }) => modelsApi.addLink(modelId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: modelKeys.detail(variables.modelId) });
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
    },
    ...options,
  });
}

export function useRetryLinkMetadata(
  options?: UseMutationOptions<void, Error, { modelId: string; linkId: string }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ modelId, linkId }) => modelsApi.retryLinkMetadata(modelId, linkId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: modelKeys.detail(variables.modelId) });
    },
    ...options,
  });
}

export function useDeleteLink(
  options?: UseMutationOptions<void, Error, { modelId: string; linkId: string }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ modelId, linkId }) => modelsApi.deleteLink(modelId, linkId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: modelKeys.detail(variables.modelId) });
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
    },
    ...options,
  });
}

export function usePreviewLinkMetadata(
  options?: UseMutationOptions<ExternalModelMetadata, Error, { url: string }>
) {
  return useMutation({
    mutationFn: ({ url }) => modelsApi.previewLinkMetadata(url),
    ...options,
  });
}

// ============================================================================
// DOMAIN HOOKS
// ============================================================================

export function useLinkDomains(options?: Omit<UseQueryOptions<string[]>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: modelKeys.linkDomains(),
    queryFn: modelsApi.getLinkDomains,
    staleTime: 60000, // Cache for 1 minute
    ...options,
  });
}

export function useRecentModels(
  options?: Omit<UseQueryOptions<PaginatedResult<ModelWithFilesAndLinks>>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: modelKeys.list({ recent: true, limit: 5, sortBy: 'updatedAt', sortOrder: 'desc' }),
    queryFn: () => modelsApi.list({ recent: true, limit: 5, sortBy: 'updatedAt', sortOrder: 'desc' }),
    staleTime: 30000, // Cache for 30 seconds like dashboard stats
    ...options,
  });
}

// Re-export types for convenience
export type { ModelQuery };
